import React from 'react';
import logoPlick from '@assets/images/logo.svg';
import { useDesktop } from '@utils/hooks/useDesktop';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import { useGetCommerce } from '@pages/Commerce/hooks/commerce';
import { useSubscription } from '@pages/Subscriptionsv2/hooks/useSubscriptions';
import { Button } from '@plick/ui';
import { OrdersCounter } from './OrdersCounter';
import { IconClock, IconPointFilled } from '@tabler/icons-react';
import FounderContactModal from '@components/interface/FounderContactModal';
import FounderMessageForm from '@components/interface/FounderMessageForm';
import { useMenuItems } from './hooks/useMenuItems';
import { closeMenu } from './utils/menuHelpers';

const HeaderV3 = () => {
  const auth = useAuth();
  const { isFreemium, isTrial, remainingTrialDays, orderLimits } = useSubscription();
  const isDesktop = useDesktop();
  const navigate = useNavigate();
  const { commerce } = useGetCommerce();
  const [showFounderWarning, setShowFounderWarning] = React.useState(false);
  const [showFounderForm, setShowFounderForm] = React.useState(false);

  // Use menu items hook
  const { mainMenuItems, createOtherMenuItems } = useMenuItems();
  const otherMenuItems = createOtherMenuItems(() => setShowFounderWarning(true));

  return (
    <>
      {/* Checkbox oculto */}
      <input type="checkbox" id="burger-toggle" />

      {/* Header superior */}
      <div className="fixed w-full z-50 bg-color-primary flex justify-between text-color-white h-16 items-center p-4 lg:px-8">
        {/* Logo */}
        <div className="flex items-baseline">
          <img src={logoPlick} alt="logo plick" height="25px" />
        </div>

        <div className="flex gap-4">
          {/* Trial Days Badge + Button - Desktop */}
          {isDesktop && (isFreemium || isTrial) && (
            <div className="flex items-center gap-3">
              {/* Badge de días restantes para trial activo */}
              {isTrial && remainingTrialDays > 0 && (
                <div className="flex items-center gap-2 bg-color-warning_yellow rounded-lg px-3 py-1.5">
                  <IconClock color="#253746" />
                  <span className="text-xs font-semibold text-color-primary whitespace-nowrap">
                    {remainingTrialDays} día{remainingTrialDays !== 1 ? 's' : ''} restante
                    {remainingTrialDays !== 1 ? 's' : ''}
                  </span>
                </div>
              )}

              {/* Botón - cambia según estado del trial */}
              <Button.Tertiary
                text={isTrial ? 'VER PLANES' : 'PROBAR GRATIS'}
                onClick={() => navigate(isTrial ? '/mi-cuenta' : '/activar-trial')}
              />
            </div>
          )}
          <div className="flex gap-6">
            {/* OrdersCounter */}
            {orderLimits?.currentMonthCount !== undefined &&
            orderLimits?.currentMonthCount !== null &&
            orderLimits?.monthlyLimit !== undefined &&
            orderLimits?.monthlyLimit !== null &&
            orderLimits?.quotaUsagePercent !== undefined &&
            orderLimits?.quotaUsagePercent !== null ? (
              <OrdersCounter
                isDesktop={isDesktop}
                current={orderLimits.currentMonthCount}
                limit={orderLimits.monthlyLimit}
                usagePercent={orderLimits.quotaUsagePercent < 100 ? orderLimits.quotaUsagePercent : 100}
              />
            ) : null}

            {/* Botón hamburguesa solo en móvil */}
            {!isDesktop && auth.isReady && (
              <label
                htmlFor="burger-toggle"
                className="header-v3-burger-container cursor-pointer p-2 hover:bg-white/10 rounded-md transition-colors"
              >
                <div className="header-v3-burger-line"></div>
                <div className="header-v3-burger-line"></div>
                <div className="header-v3-burger-line"></div>
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Overlay */}
      <label htmlFor="burger-toggle" className="header-v3-nav-overlay"></label>

      {/* Menú desplegable */}
      <nav className="header-v3-nav-menu">
        <div className="px-2">
          <section className="px-2 py-4 flex flex-col">
            {/* Sección principal */}
            <div className="bg-color-white rounded-lg p-1 mb-4">
              {mainMenuItems
                .filter((item) => item.show)
                .map((item) => (
                  <button
                    key={item.id}
                    onClick={item.onClick}
                    className="relative w-full flex items-center gap-3 p-4 text-left active:bg-color-gray_light"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div className="text-color-gray_ultra_dark">{item.icon}</div>
                    <span className="text-color-gray_ultra_dark font-medium">{item.label}</span>
                    {item.notIncludedOnCurrentPlan && (
                      <div className="absolute right-3">
                        <IconPointFilled className="text-color-secondary" size={12} />
                      </div>
                    )}
                  </button>
                ))}
            </div>
            {/* Botón Probar Gratis - Mobile Menu */}
            {(isFreemium || isTrial) && (
              <div className="mb-4 w-full">
                {/* Botón - cambia según estado del trial */}
                <Button.Tertiary
                  text={isTrial ? 'VER PLANES' : 'PROBAR GRATIS'}
                  onClick={() => {
                    navigate(isTrial ? '/mi-cuenta' : '/activar-trial');
                    closeMenu();
                  }}
                />

                {/* Badge de días restantes */}
                {isTrial && remainingTrialDays > 0 && (
                  <div className="mt-2 flex items-center justify-center gap-2 bg-color-warning_yellow rounded-lg px-3 py-1.5">
                    <IconClock size={16} color="#253746" />
                    <span className="text-sm font-semibold text-color-primary whitespace-nowrap">
                      Te quedan {remainingTrialDays} día{remainingTrialDays !== 1 ? 's' : ''}
                    </span>
                  </div>
                )}
              </div>
            )}
            {/* Separador */}
            <div className="px-4 py-3 text-center">
              <h3 className="text-sm font-medium text-color-white opacity-90">Otras opciones...</h3>
            </div>

            {/* Sección "Otras opciones" */}
            <div className="bg-color-white rounded-lg p-1">
              {otherMenuItems
                .filter((item) => item.show)
                .map((item) => (
                  <button
                    key={item.id}
                    onClick={item.onClick}
                    className="w-full flex items-center gap-3 p-4 text-left active:bg-color-gray_light"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div className={item.isRed ? 'text-color-warning' : 'text-color-gray_ultra_dark'}>{item.icon}</div>
                    <span className={`font-medium ${item.isRed ? 'text-color-warning' : 'text-color-gray_ultra_dark'}`}>
                      {item.label}
                    </span>
                  </button>
                ))}
            </div>
          </section>
        </div>
      </nav>

      {/* Modals de Feedback */}
      {showFounderWarning ? (
        <FounderContactModal
          onClose={() => setShowFounderWarning(false)}
          onContinue={() => {
            setShowFounderWarning(false);
            setShowFounderForm(true);
          }}
        />
      ) : null}
      {showFounderForm ? <FounderMessageForm commerce={commerce} onClose={() => setShowFounderForm(false)} /> : null}
    </>
  );
};

export default HeaderV3;
